/**
 * The uniform Workpay response envelope and the normalised error every API
 * call rejects with. Shared by every app that talks to a Workpay backend
 * (tenant-onboarding, ds-external) — pair this with `applyEnvelopeInterceptor`
 * from `./envelopeInterceptor`.
 */

/** Universal success status.code. Sourced from env so it is never hardcoded. */
export const SUCCESS_CODE = Number(
  import.meta.env.VITE_API_SUCCESS_CODE ?? 2000,
);

/** On-wire response shape emitted by every Workpay backend service. */
export interface ApiEnvelope<T> {
  status: {
    code: number;
    description: string;
    fields?: Record<string, string>;
  };
  data: T | null;
}

/**
 * Normalised error every non-success API outcome rejects with — whether it
 * arrived as a business error in an HTTP 200 envelope (Java/Go services) or
 * as a real 4xx/5xx from the api-gateway. `code === 0` is a synthetic
 * transport-failure code (no envelope on the wire).
 */
export class WorkpayApiError extends Error {
  constructor(
    readonly code: number,
    readonly description: string,
    readonly fields?: Record<string, string>,
    readonly httpStatus = 200,
  ) {
    super(description);
    this.name = "WorkpayApiError";
  }
}

/** Unauthorized / session-gone family — triggers a login redirect. */
const UNAUTHORIZED_CODES = new Set([4010, 48010, 48011, 48012, 48013, 48014]);

/**
 * True for an unauthorized / session-gone outcome. Matches the envelope code
 * family; also treats a wire-level HTTP 401 as unauthorized so a bare
 * (non-enveloped) 401 still triggers the login flow.
 */
export const isUnauthorizedError = (e: unknown): e is WorkpayApiError =>
  e instanceof WorkpayApiError &&
  (UNAUTHORIZED_CODES.has(e.code) || e.httpStatus === 401);

/**
 * Forbidden / insufficient-permission outcome. Matches the envelope code
 * family (4030 service K_FORBIDDEN, 48030 gateway K_INSUFFICIENT_PERMISSIONS)
 * and also a wire-level HTTP 403 — so a bare, non-enveloped 403 (e.g. from
 * nginx / a WAF / a proxy upstream of the gateway) is still caught. Duck-typed
 * so it works on any error object, not only a WorkpayApiError instance.
 */
const FORBIDDEN_CODES = new Set([4030, 48030]);
export const isForbiddenError = (e: unknown): boolean => {
  if (!e || typeof e !== "object") return false;
  const err = e as { code?: number; httpStatus?: number };
  return FORBIDDEN_CODES.has(err.code ?? -1) || err.httpStatus === 403;
};

/** Service-level "no such row" (`4040 K_NOT_FOUND`). */
export const NOT_FOUND_CODE = 4040;

/**
 * True for a not-found outcome.
 *
 * Since WP-14605 this also covers "the row exists but is not in THIS request's
 * company scope" — the scoped writes answer `4040`, deliberately not `403`,
 * because a 403 would confirm the row exists and let someone probe for ids. The
 * caller cannot tell the cases apart and does not need to: the right response to
 * all of them is to re-read rather than to insist.
 */
export const isNotFoundError = (e: unknown): boolean => {
  if (!e || typeof e !== "object") return false;
  return (e as { code?: number }).code === NOT_FOUND_CODE;
};

/**
 * All-companies mode, and the caller holds this endpoint's permission in NONE of
 * their companies (api-gateway, HTTP 403).
 *
 * Deliberately distinct from `48034 K_TENANT_SELECTION_REQUIRED`, and the two are
 * easy to confuse because both are 403s raised by the same middleware in the same
 * mode. They ask for opposite things:
 *
 *   48034 — the endpoint cannot answer across companies. PICK ONE and retry.
 *   48036 — you may not do this in ANY company. Picking one changes nothing.
 *
 * Telling the user to choose a company when no choice would help is the failure
 * this code exists to prevent, so it is worth branching on rather than letting
 * both fall into the generic forbidden copy.
 */
export const NO_QUALIFYING_TENANT_CODE = 48036;

/**
 * True for the 48036 outcome. Duck-typed on the envelope code so it works on any
 * error object — NOT on `httpStatus`, which every other 403 also carries and
 * which would make this indistinguishable from the thing it exists to separate.
 */
export const isNoQualifyingTenantError = (e: unknown): boolean => {
  if (!e || typeof e !== "object") return false;
  return (e as { code?: number }).code === NO_QUALIFYING_TENANT_CODE;
};

/** Session host-binding mismatch — the session is bound to a different subdomain
 *  than the one the request came from (api-gateway emits this as HTTP 200,
 *  envelope code 48015 / K_SESSION_HOST_MISMATCH). */
export const SESSION_HOST_MISMATCH_CODE = 48015;

/**
 * True for a session-host-mismatch outcome. Duck-typed on the envelope code so
 * it works on any error object, not only a WorkpayApiError instance.
 */
export const isSessionHostMismatchError = (e: unknown): boolean => {
  if (!e || typeof e !== "object") return false;
  return (e as { code?: number }).code === SESSION_HOST_MISMATCH_CODE;
};
